import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  claims: [],
  archivedClaims: [],
  claim: null,
  loading: false,
  isSuccess: false,
  message: '',
  errors: [],
  dashboardStats: null,
  statsLoading: false,
  statsError: null
};

export const fetchClaims = createAsyncThunk(
  'claims/fetchClaims',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim', { params });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimByID = createAsyncThunk(
  'claims/fetchClaimByID',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/getDetailClaims/${id}`);
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessage = createAsyncThunk(
  'claims/addMessage',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-message`, { message });
      await dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessagetreated = createAsyncThunk(
  'claims/addMessagetreated',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add_message_treated`, {
        message
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const AddMediaMessage = createAsyncThunk(
  'claim/AddMediaMessage',
  async (params, { rejectWithValue, dispatch }) => {
    const { formData, id } = params;
    try {
      const response = await api.post(`/claim/${id}/add-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStatusClaim = createAsyncThunk(
  'claims/updateStatusClaim',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const { id, status } = params;
      const response = await api
        .put(`/claim/${id}/${status}/update-status`)
        .then(() => fetchClaims());
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchArchivedClaimsByCompany = createAsyncThunk(
  'claims/fetchArchivedClaimsByCompany',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/archived/company/${companyId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const unarchive = createAsyncThunk(
  'claims/unarchive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/unarchive`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const archive = createAsyncThunk(
  'claims/archive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/archive`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimBarchivedAll = createAsyncThunk(
  'claim/fetchClaimBarchivedAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim/archivedAll/');
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockClient = createAsyncThunk(
  'user/block',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/block/${claim.added_by[0]?._id}`);
      return fetchClaimByID(claim?._id);
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const unblockClient = createAsyncThunk(
  'user/unblock',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/user/unblock/${claim.added_by[0]?._id}`);
      return fetchClaimByID(claim?._id);
    } catch (error) {
      if (error.response.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'claims/deleteMessage',
  async ({ claimId, actionId }, { rejectWithValue, dispatch }) => {
    try {
      if (!claimId || !actionId) {
        throw new Error('Les paramètres claimId ou actionId sont manquants');
      }

      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await api.delete(
        `/claim/${claimId}/delete/${actionId}`,
        { headers }
      );

      dispatch(fetchClaimByID(claimId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur inconnue');
    }
  }
);

export const markActionsViewed = createAsyncThunk(
  'claims/markActionsViewed',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/claim/${claimId}/actions-viewed`);
      const { payload } = await dispatch(fetchClaimByID(claimId));
      return payload;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'claims/fetchDashboardStats',
  async ({ startDate, endDate } = {}, { rejectWithValue, dispatch }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/stats/dashboard', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        dispatch(logout());
      }
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaims.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClaims.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claims = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchClaims.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(fetchClaimByID.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClaimByID.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claim = payload;
        state.loading = false;
      })
      .addCase(fetchClaimByID.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(blockClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(blockClient.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(blockClient.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(unblockClient.pending, (state) => {
        state.loading = true;
      })
      .addCase(unblockClient.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(unblockClient.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(addMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(addMessage.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.message = payload;
        state.loading = false;
      })
      .addCase(addMessage.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(AddMediaMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(AddMediaMessage.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(AddMediaMessage.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(updateStatusClaim.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateStatusClaim.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(updateStatusClaim.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(archive.pending, (state) => {
        state.loading = true;
      })
      .addCase(archive.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(archive.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(unarchive.pending, (state) => {
        state.loading = true;
      })
      .addCase(unarchive.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(unarchive.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(addMessagetreated.pending, (state) => {
        state.loading = true;
      })
      .addCase(addMessagetreated.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(addMessagetreated.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(deleteMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMessage.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(deleteMessage.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        } else {
          state.errors = ['Error deleting message'];
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(markActionsViewed.pending, (state) => {
        state.loading = true;
      })
      .addCase(markActionsViewed.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.claim = payload;
        state.loading = false;
      })
      .addCase(markActionsViewed.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        } else {
          state.errors = ['Error marking actions as viewed'];
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, { payload }) => {
        state.statsLoading = false;
        state.dashboardStats = payload;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.rejected, (state, { payload }) => {
        state.statsLoading = false;
        state.statsError = payload?.message || 'Failed to fetch dashboard stats';
        state.dashboardStats = null;
      })
      .addCase(fetchArchivedClaimsByCompany.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchArchivedClaimsByCompany.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.archivedClaims = payload;
        state.loading = false;
      })
      .addCase(fetchArchivedClaimsByCompany.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      });
  }
});

export default claimsSlice.reducer;